// The log's filters, as one compact bar: a search box that reads what is pasted into it, the kind of
// actor, a time range, and "More filters" — a dialog with the rarer fields and the request trace.
// Every filter in use is a chip under the bar that removes it, with "Clear all" at the end.
//
// The quick ranges (Today, Yesterday, the last 7 or 30 days) are calendar days in a reference zone —
// a filtered venue's, or the reader's — resolved by the log itself; the bar only names the range it
// chose. A filter is applied when it is chosen, so the search box and the dialog read only when
// submitted, never as they are typed.

import { useTranslation } from '@clavian/i18n/react';
import { Badge, Button, Chip, Dialog, SegmentedControl, Select, TextField } from '@clavian/ui-web';
import { useId, useState, type FormEvent, type ReactNode } from 'react';

import { Icon } from '../../shell/nav-icons';
import { SearchField } from '../../ui';
import {
  AUDIT_ACTOR_KINDS,
  filterFromForm,
  formFromFilter,
  isRequestId,
  MORE_FILTER_KEYS,
  parseSearch,
  shortId,
  without,
  type AuditFilter,
  type AuditFilterKey,
  type AuditRange,
  type FilterForm,
  type FilterProblem,
} from './filters';
import type { AuditLabels, Directory } from './labels';
import type { AuditTimes } from './time';

export interface AuditView {
  readonly filter: AuditFilter;
  readonly range: AuditRange;
}

const PROBLEM_WORDS: Readonly<Record<FilterProblem, string>> = {
  venue: 'audit.filters.invalidVenue',
  from: 'audit.filters.invalidInstant',
  to: 'audit.filters.invalidInstant',
  window: 'audit.filters.window',
};

const QUICK_RANGES = ['today', 'yesterday', 'last7', 'last30', 'all', 'custom'] as const satisfies readonly AuditRange[];

export interface FilterBarProps {
  readonly view: AuditView;
  readonly labels: AuditLabels;
  readonly directory: Directory;
  readonly times: AuditTimes;
  /** The reference zone the quick ranges are computed in, named for the reader. */
  readonly zoneLabel: string;
  /** A player's tab fixes the target, so its fields and chips are left out of the bar. */
  readonly hideTarget: boolean;
  readonly onFilter: (filter: AuditFilter) => void;
  readonly onRange: (range: AuditRange) => void;
  /** The window from the dialog: `custom`, unless it is what the quick range already set. */
  readonly onApply: (filter: AuditFilter) => void;
  readonly onTrace: (requestId: string) => void;
}

export function FilterBar({ view, labels, directory, times, zoneLabel, hideTarget, onFilter, onRange, onApply, onTrace }: FilterBarProps): ReactNode {
  const { t } = useTranslation('console');
  const [text, setText] = useState('');
  const [searchError, setSearchError] = useState<string | undefined>(undefined);
  const [moreOpen, setMoreOpen] = useState(false);
  const errorId = useId();
  const { filter } = view;
  const moreKeys = hideTarget ? MORE_FILTER_KEYS.filter((key) => key !== 'targetType' && key !== 'targetId') : MORE_FILTER_KEYS;
  const moreCount = moreKeys.filter((key) => filter[key] !== undefined && !(view.range !== 'custom' && (key === 'from' || key === 'to'))).length;

  const search = (event: FormEvent) => {
    event.preventDefault();
    if (text.trim() === '') return;
    const term = parseSearch(text, labels.actionsByLabel);
    if (term === undefined) {
      setSearchError(t('audit.filters.searchInvalid'));
      return;
    }
    setSearchError(undefined);
    setText('');
    if (term.key === 'requestId') onTrace(term.value);
    else onFilter({ ...filter, [term.key]: term.value });
  };

  const chips = activeChips(view, { word: (key, values) => String(t(key, values ?? {})), labels, directory, times }, hideTarget);

  return (
    <div className="console-audit-filters">
      <div className="console-audit-bar">
        <form role="search" className="console-audit-search" aria-label={t('audit.filters.bar')} noValidate onSubmit={search}>
          <SearchField
            label={t('audit.filters.search')}
            placeholder={t('audit.filters.searchPlaceholder')}
            value={text}
            onChange={(event) => {
              setText(event.target.value);
              if (searchError !== undefined) setSearchError(undefined);
            }}
            aria-invalid={searchError === undefined ? undefined : true}
            aria-describedby={searchError === undefined ? undefined : errorId}
          />
        </form>
        <Select
          aria-label={t('audit.filters.actorKind')}
          className="console-audit-kind-select"
          value={filter.actorKind ?? 'any'}
          options={[
            { value: 'any', label: t('audit.filters.anyActor') },
            ...AUDIT_ACTOR_KINDS.map((kind) => ({ value: kind, label: t(`audit.actorKind.${kind}`) })),
          ]}
          onValueChange={(value) => onFilter(value === 'any' ? without(filter, 'actorKind') : { ...filter, actorKind: value })}
        />
        <SegmentedControl<AuditRange>
          size="sm"
          className="console-audit-range"
          label={t('audit.filters.range.label', { zone: zoneLabel })}
          value={view.range}
          options={QUICK_RANGES.map((range) => ({ value: range, label: t(`audit.filters.range.${range}`) }))}
          onValueChange={(range) => {
            // A custom window is written in the dialog; the quick range stays as it is until it is applied.
            if (range === 'custom') setMoreOpen(true);
            else onRange(range);
          }}
        />
        <Button size="sm" className="console-audit-more" icon={<Icon name="list" />} onClick={() => setMoreOpen(true)}>
          {t('audit.filters.more')}
          {moreCount === 0 ? null : <Badge tone="accent">{moreCount}</Badge>}
        </Button>
      </div>
      {searchError === undefined ? null : (
        <p id={errorId} className="console-audit-error" role="alert">
          {searchError}
        </p>
      )}
      {chips.length === 0 ? null : (
        <ul className="console-audit-chips" aria-label={t('audit.filters.inUse')}>
          {chips.map((chip) => (
            <li key={chip.key}>
              <Chip onRemove={() => onFilter(without(filter, ...chip.keys))} removeLabel={chip.label}>
                {chip.label}
              </Chip>
            </li>
          ))}
          <li>
            <Button variant="ghost" size="sm" onClick={() => onFilter({})}>
              {t('audit.filters.clearAll')}
            </Button>
          </li>
        </ul>
      )}
      <MoreFilters
        open={moreOpen}
        onOpenChange={setMoreOpen}
        view={view}
        directory={directory}
        hideTarget={hideTarget}
        onApply={(next) => {
          setMoreOpen(false);
          onApply(next);
        }}
        onTrace={(requestId) => {
          setMoreOpen(false);
          onTrace(requestId);
        }}
      />
    </div>
  );
}

interface ChipWords {
  readonly word: (key: string, values?: Readonly<Record<string, string>>) => string;
  readonly labels: AuditLabels;
  readonly directory: Directory;
  readonly times: AuditTimes;
}

interface ActiveChip {
  readonly key: string;
  readonly label: string;
  /** What removing it takes off the filter. */
  readonly keys: readonly AuditFilterKey[];
}

function activeChips({ filter, range }: AuditView, { word: t, labels, directory, times }: ChipWords, hideTarget: boolean): ActiveChip[] {
  const chips: ActiveChip[] = [];
  const add = (key: AuditFilterKey, value: string) => chips.push({ key, label: t(`audit.filters.chip.${key}`, { value }), keys: [key] });
  if (filter.venueId !== undefined) add('venueId', directory.venue(filter.venueId) ?? shortId(filter.venueId));
  if (filter.action !== undefined) add('action', filter.action.endsWith('.*') ? filter.action : labels.action(filter.action));
  if (filter.actorKind !== undefined) add('actorKind', t(`audit.actorKind.${filter.actorKind}`));
  if (filter.actorId !== undefined) add('actorId', shortId(filter.actorId));
  if (!hideTarget && filter.targetType !== undefined) add('targetType', labels.targetType(filter.targetType));
  if (!hideTarget && filter.targetId !== undefined) add('targetId', shortId(filter.targetId));
  if (filter.onBehalfOf !== undefined) add('onBehalfOf', shortId(filter.onBehalfOf));
  if (range === 'today' || range === 'yesterday' || range === 'last7' || range === 'last30') {
    chips.push({ key: 'range', label: t(`audit.filters.chip.${range}`), keys: ['from', 'to'] });
  } else {
    if (filter.from !== undefined) add('from', times.utcShort(filter.from));
    if (filter.to !== undefined) add('to', times.utcShort(filter.to));
  }
  return chips;
}

// ---------------------------------------------------------------------------------------------
// More filters
// ---------------------------------------------------------------------------------------------

interface MoreFiltersProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly view: AuditView;
  readonly directory: Directory;
  readonly hideTarget: boolean;
  readonly onApply: (filter: AuditFilter) => void;
  readonly onTrace: (requestId: string) => void;
}

function MoreFilters({ open, onOpenChange, view, directory, hideTarget, onApply, onTrace }: MoreFiltersProps): ReactNode {
  const { t } = useTranslation('console');
  const formId = useId();
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      title={t('audit.filters.more')}
      description={t('audit.filters.moreDescription')}
      footer={
        <>
          <Button type="submit" form={formId} variant="primary">
            {t('audit.filters.apply')}
          </Button>
          <Button onClick={() => onApply({})}>{t('audit.filters.clear')}</Button>
        </>
      }
    >
      {/* Mounted only while open: each opening starts from the filter as it is applied. */}
      {open ? (
        <div className="console-audit-more-body">
          <FiltersForm id={formId} view={view} directory={directory} hideTarget={hideTarget} onApply={onApply} />
          <TraceForm onTrace={onTrace} />
        </div>
      ) : null}
    </Dialog>
  );
}

function FiltersForm({ id, view, directory, hideTarget, onApply }: { readonly id: string; readonly view: AuditView; readonly directory: Directory; readonly hideTarget: boolean; readonly onApply: (filter: AuditFilter) => void }) {
  const { t } = useTranslation('console');
  const [form, setForm] = useState<FilterForm>(() => formFromFilter(view.filter));
  const [problems, setProblems] = useState<Partial<Record<AuditFilterKey, FilterProblem>>>({});
  const venuesList = useId();

  const field = (key: Exclude<AuditFilterKey, 'actorKind' | 'requestId'>, options: { type?: string; description?: string; list?: string } = {}) => {
    const problem = problems[key];
    return (
      <TextField
        label={t(`audit.filters.${key}`)}
        value={form[key]}
        autoComplete="off"
        spellCheck={false}
        {...(options.type === undefined ? {} : { type: options.type })}
        {...(options.list === undefined ? {} : { list: options.list })}
        {...(options.description === undefined ? {} : { description: options.description })}
        {...(problem === undefined ? {} : { error: t(PROBLEM_WORDS[problem]) })}
        onChange={(event) => setForm({ ...form, [key]: event.target.value })}
      />
    );
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const checked = filterFromForm(form);
    setProblems(checked.problems);
    if (Object.keys(checked.problems).length === 0) onApply(checked.filter);
  };

  return (
    <form id={id} className="console-audit-form" noValidate onSubmit={submit}>
      <fieldset className="console-audit-fieldset">
        <legend>{t('audit.filters.groups.where')}</legend>
        {field('venueId', directory.venues.length === 0 ? {} : { list: venuesList })}
      </fieldset>
      <fieldset className="console-audit-fieldset">
        <legend>{t('audit.filters.groups.who')}</legend>
        {field('action', { description: t('audit.filters.actionDescription') })}
        {field('actorId')}
        {hideTarget ? null : field('targetType', { description: t('audit.filters.targetTypeDescription') })}
        {hideTarget ? null : field('targetId')}
        {field('onBehalfOf', { description: t('audit.filters.onBehalfOfDescription') })}
      </fieldset>
      <fieldset className="console-audit-fieldset">
        <legend>{t('audit.filters.groups.when')}</legend>
        {field('from', { type: 'datetime-local', description: t('audit.filters.fromDescription') })}
        {field('to', { type: 'datetime-local', description: t('audit.filters.toDescription') })}
      </fieldset>
      {/* Suggestions for the venue id field: the organisation's venues this member knows by name. */}
      <datalist id={venuesList}>
        {directory.venues.map((venue) => (
          <option key={venue.id} value={venue.id}>
            {venue.name}
          </option>
        ))}
      </datalist>
    </form>
  );
}

function TraceForm({ onTrace }: { readonly onTrace: (requestId: string) => void }) {
  const { t } = useTranslation('console');
  const [text, setText] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const titleId = useId();
  return (
    <form
      className="console-audit-trace-form"
      role="search"
      aria-labelledby={titleId}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        const requestId = text.trim().toLowerCase();
        if (!isRequestId(requestId)) {
          setError(t('audit.trace.invalid'));
          return;
        }
        setError(undefined);
        onTrace(requestId);
      }}
    >
      <h3 id={titleId} className="console-audit-group__title">
        {t('audit.trace.title')}
      </h3>
      <TextField
        label={t('audit.trace.label')}
        description={t('audit.trace.description')}
        value={text}
        autoComplete="off"
        spellCheck={false}
        onChange={(event) => setText(event.target.value)}
        {...(error === undefined ? {} : { error })}
      />
      <div>
        <Button type="submit" size="sm">
          {t('audit.trace.submit')}
        </Button>
      </div>
    </form>
  );
}
